import {
  VendorCreditForeignEditor,
  type VendorCreditForeignEditorHandle,
} from './vendor-credit-foreign-editor';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  vendorCreditReviewSaveSchema,
  vendorCreditOffsetSchema,
  vendorCreditOffsetReverseSchema,
  vendorCreditOffsetResultSchema,
  vendorCreditOffsetHistorySchema,
  vendorInvoiceDetailSchema,
  vendorInvoiceListResponseSchema,
  ledgerWorkspaceSchema,
  compareDecimal,
  type VendorInvoiceDetail,
  type AssetCreditReview,
  type VendorCreditReview,
} from '@erp/shared';
import { VendorAssetCreditEditor } from './vendor-asset-credit-editor';
import { usePermissions } from '@/features/auth';
import { FinancialFormProtection, formatLedgerMoney } from '@/features/accounting';
import { InlineHelp } from '@/features/help';
import { apiFetch } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
const editorSchema = z.record(z.string());
const base = '/api/v1/procurement/vendor-invoices';
type Mode = 'review' | 'offset' | 'reverse';
export function VendorCreditPanel({ invoice }: { invoice: VendorInvoiceDetail }) {
  const { t, i18n } = useTranslation(),
    { can } = usePermissions();
  const [mode, setMode] = useState<Mode | null>(null),
    [reverseId, setReverseId] = useState('');
  const [page, setPage] = useState(1);
  const read = can('purchase:read') && can('ledger:read');
  const negative = compareDecimal(invoice.grossAmount, '0') < 0;
  const posted = ['APPROVED_FOR_PAYMENT', 'PAID'].includes(invoice.status);
  const hasHistory = posted || invoice.status === 'CANCELLED';
  const history = useQuery({
    queryKey: ['supplier-credits', invoice.id, page],
    enabled: read && negative && hasHistory,
    queryFn: () =>
      apiFetch(
        `${base}/${invoice.id}/credit-offsets?page=${page}&pageSize=25`,
        vendorCreditOffsetHistorySchema,
      ),
  });
  if (!negative || !read) return null;
  return (
    <section
      className="space-y-3 rounded border bg-card p-3"
      aria-label={t('supplierCredits.title')}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">{t('supplierCredits.title')}</h2>
        <InlineHelp articleId="procurement.supplier-credits" label={t('ledger.help')} />
        {!posted &&
        invoice.status !== 'CANCELLED' &&
        can('purchase:write') &&
        can('ledger:write') ? (
          <Button variant="outline" onClick={() => setMode('review')}>
            {t('supplierCredits.review')}
          </Button>
        ) : null}
        {posted && can('purchase:write') && can('ledger:post') ? (
          <Button variant="outline" onClick={() => setMode('offset')}>
            {t('supplierCredits.offset')}
          </Button>
        ) : null}
        {posted && can('banking:read') && can('banking:post') ? (
          <Button asChild variant="outline">
            <Link
              to={`/finance/payments?${new URLSearchParams({ party: 'VENDOR', partnerId: invoice.supplierId, partnerLabel: invoice.supplierName, vendorInvoiceId: invoice.id, currency: invoice.currency, direction: 'CREDIT', reference: invoice.invoiceNumber })}`}
            >
              {t('ledgerPayments.new')}
            </Link>
          </Button>
        ) : null}
      </div>
      <p className="text-sm text-muted-foreground">{t('supplierCredits.timing')}</p>
      <p className="text-sm">{t('supplierCredits.refund')}</p>
      {invoice.creditReview ? (
        <p className="text-sm">
          <Link
            className="underline"
            to={`/procurement/vendor-invoices/${invoice.creditReview.sourceInvoiceId}/match`}
          >
            {t('supplierCredits.source')}
          </Link>{' '}
          · {invoice.creditReview.reason}
        </p>
      ) : null}
      {history.isError ? (
        <div role="alert">
          <p>{t(history.error.message)}</p>
          <Button variant="outline" onClick={() => void history.refetch()}>
            {t('supplierCredits.retry')}
          </Button>
        </div>
      ) : null}
      {hasHistory && history.isPending ? <p role="status">{t('supplierCredits.loading')}</p> : null}
      {history.data?.data.length ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('supplierCredits.date')}</TableHead>
                <TableHead>{t('supplierCredits.target')}</TableHead>
                <TableHead>{t('supplierCredits.amount')}</TableHead>
                <TableHead>{t('supplierCredits.reason')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.data.data.map((row) => (
                <TableRow key={row.creditSettlementId}>
                  <TableCell>{row.effectiveDate}</TableCell>
                  <TableCell>
                    <Link
                      className="underline"
                      to={`/procurement/vendor-invoices/${row.targetInvoiceId}/match`}
                    >
                      {t('supplierCredits.target')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {formatLedgerMoney(row.amount, invoice.currency, i18n.language)}
                  </TableCell>
                  <TableCell>{row.reason}</TableCell>
                  <TableCell>
                    {!row.reversed && can('purchase:write') && can('ledger:reverse') ? (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setReverseId(row.creditSettlementId);
                          setMode('reverse');
                        }}
                      >
                        {t('ledger.reverse')}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >
              {t('common.previous')}
            </Button>
            <span>
              {page} / {history.data.meta.totalPages}
            </span>
            <Button
              variant="outline"
              disabled={page >= history.data.meta.totalPages}
              onClick={() => setPage((value) => value + 1)}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      ) : null}
      {mode ? (
        <VendorCreditDialog
          key={`${mode}:${reverseId}`}
          invoice={invoice}
          mode={mode}
          reverseId={reverseId}
          onClose={() => setMode(null)}
        />
      ) : null}
    </section>
  );
}
function VendorCreditDialog({
  invoice,
  mode,
  reverseId,
  onClose,
}: {
  invoice: VendorInvoiceDetail;
  mode: Mode;
  reverseId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation(),
    prefix = useId(),
    client = useQueryClient();
  const [error, setError] = useState(''),
    [query, setQuery] = useState(''),
    [complete, setComplete] = useState(false);
  const retryKey = useRef(crypto.randomUUID());
  const foreignEditor = useRef<VendorCreditForeignEditorHandle>(null);
  const form = useForm<Record<string, string>>({
    resolver: zodResolver(editorSchema),
    defaultValues: {
      sourceInvoiceId: invoice.creditReview?.sourceInvoiceId ?? '',
      reason: invoice.creditReview?.reason ?? '',
      taxClearingAccountId: invoice.creditReview?.taxClearingAccountId ?? '',
      zeroVatSourceCode: invoice.creditReview?.zeroVatSourceCode ?? '',
      targetInvoiceId: '',
      amount: '',
      effectiveDate: '',
    },
  });
  const [assetReview, setAssetReview] = useState<AssetCreditReview | null>(
    invoice.creditReview?.assetReview ?? null,
  );
  const [foreignFiscalReview, setForeignFiscalReview] = useState<
    VendorCreditReview['foreignFiscalReview']
  >(invoice.creditReview?.foreignFiscalReview ?? null);
  const foreignReviewChanged = useCallback(
    (value: VendorCreditReview['foreignFiscalReview'], changed: boolean) => {
      setForeignFiscalReview(value);
      if (changed) form.setValue('foreignReviewChanged', 'yes', { shouldDirty: true });
    },
    [form],
  );
  const sourceInvoiceId = form.watch('sourceInvoiceId') ?? '';
  const assetReviewChanged = useCallback(
    (value: AssetCreditReview | null, changed: boolean) => {
      setAssetReview(value);
      if (changed) form.setValue('assetReviewChanged', 'yes', { shouldDirty: true });
    },
    [form],
  );
  const workspace = useQuery({
    queryKey: ['supplier-credit-workspace'],
    queryFn: () => apiFetch('/api/v1/accounting/ledger/workspace', ledgerWorkspaceSchema),
  });
  useEffect(() => {
    if (workspace.data && !form.getValues('effectiveDate'))
      form.setValue('effectiveDate', workspace.data.businessDate);
  }, [workspace.data, form]);
  const sources = useQuery({
    queryKey: ['supplier-credit-sources', invoice.supplierId, query],
    enabled: mode !== 'reverse',
    queryFn: () =>
      apiFetch(
        `${base}?${new URLSearchParams({ supplierId: invoice.supplierId, q: query, pageSize: '50', status: 'APPROVED_FOR_PAYMENT,PAID' })}`,
        vendorInvoiceListResponseSchema,
      ),
  });
  const mutation = useMutation({
    mutationFn: async (values: Record<string, string>) => {
      const parsed =
        mode === 'review'
          ? vendorCreditReviewSaveSchema.safeParse({
              sourceInvoiceId: values['sourceInvoiceId'],
              reason: values['reason'],
              taxClearingAccountId: values['taxClearingAccountId'],
              version: invoice.version,
              zeroVatSourceCode: values['zeroVatSourceCode'] || null,
              assetReview,
              foreignFiscalReview,
            })
          : mode === 'offset'
            ? vendorCreditOffsetSchema.safeParse({
                ...values,
                amount: values['amount']?.trim().replace(',', '.'),
                idempotencyKey: retryKey.current,
              })
            : vendorCreditOffsetReverseSchema.safeParse({
                ...values,
                idempotencyKey: retryKey.current,
              });
      if (!parsed.success) {
        for (const issue of parsed.error.issues)
          form.setError(String(issue.path[0] ?? 'reason'), {
            message: t('ledgerClosing.validation'),
          });
        const first = parsed.error.issues[0];
        if (first?.path[0] === 'foreignFiscalReview')
          foreignEditor.current?.focusInvalid(first.path.slice(1));
        else if (first?.path[0]) form.setFocus(String(first.path[0]));
        throw new Error('ledgerClosing.validation');
      }
      if (mode === 'review')
        return apiFetch(`${base}/${invoice.id}/credit-review`, vendorInvoiceDetailSchema, {
          method: 'PUT',
          body: parsed.data,
        });
      const path = mode === 'offset' ? 'credit-offsets' : `credit-offsets/${reverseId}/reverse`;
      return apiFetch(`${base}/${invoice.id}/${path}`, vendorCreditOffsetResultSchema, {
        method: 'POST',
        body: parsed.data,
      });
    },
    onSuccess: async () => {
      setComplete(true);
      await Promise.all(
        [
          ['procurement'],
          ['supplier-credits'],
          ['vendor-invoice-accounting'],
          ['ledger'],
          ['financial-open-items'],
          ['ledger-payments'],
          ['ledger-closing'],
          ['asset-credit-source'],
        ].map((queryKey) => client.invalidateQueries({ queryKey })),
      );
      onClose();
    },
    onError: (failure) => setError(t(failure.message)),
  });
  const submit = form.handleSubmit((values) => {
    setError('');
    mutation.mutate(values);
  });
  const field = (name: string, label: string, type = 'text') => (
    <div className="space-y-1">
      <Label htmlFor={`${prefix}-${name}`}>{t(`supplierCredits.${label}`)}</Label>
      <Input id={`${prefix}-${name}`} type={type} {...form.register(name)} />
      {form.formState.errors[name] ? (
        <p role="alert" className="text-sm text-destructive">
          {form.formState.errors[name]?.message}
        </p>
      ) : null}
    </div>
  );
  const title = mode === 'review' ? 'review' : mode === 'offset' ? 'offset' : 'cancel';
  return (
    <FinancialFormProtection
      dirty={!complete && form.formState.isDirty}
      pending={mutation.isPending}
      onClose={onClose}
    >
      {(close) => (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) close();
          }}
        >
          <DialogContent
            closeLabel={t('common.close')}
            className="max-h-[90dvh] overflow-y-auto"
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              close();
            }}
          >
            <DialogTitle>
              {mode === 'reverse' ? t('ledger.reverse') : t(`supplierCredits.${title}`)}
            </DialogTitle>
            <DialogDescription>
              {t(`supplierCredits.${mode === 'review' ? 'explain' : 'setoffExplain'}`)}
            </DialogDescription>
            <form
              className="space-y-3"
              onSubmit={(event) => void submit(event)}
              onKeyDown={(event) => {
                if ((event.ctrlKey || event.metaKey) && ['s', 'Enter'].includes(event.key)) {
                  event.preventDefault();
                  void submit();
                }
              }}
            >
              {mode !== 'reverse' ? (
                <>
                  <Label htmlFor={`${prefix}-search`}>{t('common.search')}</Label>
                  <Input
                    id={`${prefix}-search`}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                  <Label htmlFor={`${prefix}-source`}>
                    {t(`supplierCredits.${mode === 'review' ? 'source' : 'target'}`)}
                  </Label>
                  <select
                    className="min-h-11 w-full rounded border bg-background px-2"
                    id={`${prefix}-source`}
                    {...form.register(mode === 'review' ? 'sourceInvoiceId' : 'targetInvoiceId')}
                  >
                    <option value="">—</option>
                    {sources.data?.data
                      .filter(
                        (row) =>
                          row.currency === invoice.currency &&
                          compareDecimal(row.grossAmount, '0') > 0,
                      )
                      .map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.invoiceNumber} · {row.invoiceDate} · {row.grossAmount} {row.currency}
                        </option>
                      ))}
                  </select>
                  {sources.isError ? (
                    <div role="alert">
                      {t(sources.error.message)}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => void sources.refetch()}
                      >
                        {t('supplierCredits.retry')}
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : null}
              {mode === 'review' ? (
                <>
                  {invoice.currency !== 'EUR' ? (
                    <VendorCreditForeignEditor
                      ref={foreignEditor}
                      key={`foreign:${sourceInvoiceId}`}
                      sourceInvoiceId={sourceInvoiceId}
                      creditId={invoice.id}
                      initial={
                        invoice.creditReview?.sourceInvoiceId === sourceInvoiceId
                          ? (invoice.creditReview.foreignFiscalReview ?? null)
                          : null
                      }
                      onChange={foreignReviewChanged}
                    />
                  ) : null}
                  <VendorAssetCreditEditor
                    key={sourceInvoiceId}
                    sourceInvoiceId={sourceInvoiceId}
                    invoice={invoice}
                    initial={
                      invoice.creditReview?.sourceInvoiceId === sourceInvoiceId
                        ? (invoice.creditReview.assetReview ?? null)
                        : null
                    }
                    onChange={assetReviewChanged}
                  />
                  <Label htmlFor={`${prefix}-clearing`}>{t('supplierCredits.clearing')}</Label>
                  <select
                    className="min-h-11 w-full rounded border bg-background px-2"
                    id={`${prefix}-clearing`}
                    {...form.register('taxClearingAccountId')}
                  >
                    <option value="">—</option>
                    {workspace.data?.accounts
                      .filter(
                        (row) => row.kind === 'LIABILITY' && row.role === 'GENERAL' && row.active,
                      )
                      .map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.number} · {row.name}
                        </option>
                      ))}
                  </select>
                  <Label htmlFor={`${prefix}-zero`}>{t('supplierCredits.zeroCode')}</Label>
                  <select
                    className="min-h-11 w-full rounded border bg-background px-2"
                    id={`${prefix}-zero`}
                    {...form.register('zeroVatSourceCode')}
                  >
                    <option value="">—</option>
                    {['EXEMPT', 'SMALL_BUSINESS', 'OUT_OF_SCOPE', 'ZERO_RATE', 'SELF_ASSESSED'].map(
                      (code) => (
                        <option key={code} value={code}>
                          {t(`supplierCredits.${code}`)}
                        </option>
                      ),
                    )}
                  </select>
                </>
              ) : (
                <>
                  {mode === 'offset' ? field('amount', 'amount') : null}
                  {field('effectiveDate', 'date', 'date')}
                </>
              )}
              {field('reason', 'reason')}
              {error ? (
                <p role="alert" className="text-destructive">
                  {error}
                </p>
              ) : null}
              {workspace.isError ? (
                <div role="alert">
                  {t(workspace.error.message)}
                  <Button type="button" onClick={() => void workspace.refetch()}>
                    {t('supplierCredits.retry')}
                  </Button>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={close}>
                  {t('supplierCredits.cancel')}
                </Button>
                <Button type="submit" disabled={mutation.isPending || workspace.isPending}>
                  {mode === 'reverse'
                    ? t('ledger.reverse')
                    : t(`supplierCredits.${mode === 'review' ? 'save' : 'confirm'}`)}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </FinancialFormProtection>
  );
}
